import axios from "axios";
import { store } from "../store/store";
import { Logout } from "../store/actions/AuthActions";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_DEV_API,
});

// Request interceptor to add token to headers
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const superAdminUser = JSON.parse(localStorage.getItem("superAdminUser"));

    const isSuperAdmin = window.location.pathname.includes('super-admin');

    // Get access token from either userDetails or superAdminUser
    // const accessToken = userDetails?.session?.accessToken || superAdminUser?.session?.accessToken;
    const accessToken = isSuperAdmin
      ? superAdminUser?.session?.accessToken
      : userDetails?.session?.accessToken;

    // If token exists, add it to headers
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check for account deactivation error first
    const errorMessage = error.response?.data?.message || "";
    if (error.response?.status === 401 && errorMessage.includes("deactivated")) {
      if (!window.isDeactivatedRedirecting) {
        window.isDeactivatedRedirecting = true;
        localStorage.clear();
        window.location.href = "/deactivated-account";
      }
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get refresh token from localStorage
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        const superAdminUser = JSON.parse(
          localStorage.getItem("superAdminUser")
        );
        const refreshToken =
          userDetails?.session?.refreshToken ||
          superAdminUser?.session?.refreshToken;

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Try to refresh the token
        const response = await axios.post(
          `${process.env.REACT_APP_DEV_API}/v1/iam/update-session`,
          { refreshToken }
        );

        if (!response?.data?.data?.session?.accessToken) {
          throw new Error("No response data");
        }

        const { accessToken, refreshToken: newRefreshToken } = response.data.data.session;

        // Update localStorage with new tokens
        if (userDetails) {
          userDetails.session.accessToken = accessToken;
          userDetails.session.refreshToken = newRefreshToken;
          localStorage.setItem("userDetails", JSON.stringify(userDetails));
        } else if (superAdminUser) {
          superAdminUser.session.accessToken = accessToken;
          superAdminUser.session.refreshToken = newRefreshToken;
          localStorage.setItem(
            "superAdminUser",
            JSON.stringify(superAdminUser)
          );
        }

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, logout user
        store.dispatch(Logout());
        localStorage.clear();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
